import React from 'react';
import heroRight from '../assets/hero_right.png';
import AvatarGroup from './AvatarGroup';
import useFunnelNavigation from '../hooks/useFunnelNavigation';
import useMobile from '../helpers/useMobile';
import Checklist from './upsell/UpsellsChecklist';
import { getBrand } from '../utils/getBrand';

const HeroSectionSlider = ({
	page,
	content,
	firstButtonText,
	checklistContent,
	secondButtonText,
	modifiers,
	displayChecklist = false,
	showFirstButton = true,
	showSecondButton = true,
	// Optional node rendered in place of the baked scrolling image (e.g. StoryMarquee).
	media,
}) => {
	const { navigateToQuiz, navigateToCheckout } = useFunnelNavigation();
	const isMobile = useMobile();
	const { brandName } = getBrand();

	const headingStyles = modifiers?.textOneStyle?.fontSmall
		? 'text-[33px] md:text-[60px] font-bold tracking-tight mb-2 md:mb-6 leading-[44px] md:leading-[75px]'
		: 'text-[40px] md:text-[66px] font-bold tracking-tight mb-6 leading-[48px] md:leading-[80px]';

	const textThreeStyles = modifiers?.textThreeStyles?.color === 'black' ? 'text-[#000]' : 'text-gray-600';

	const handleScrollToHowItWorks = (e) => {
		e.preventDefault();
		const element = document.getElementById('how-it-works');
		if (element) {
			element.scrollIntoView({ behavior: 'smooth' });
		}
	};

	const defaultContent = {
		textOne: 'Same job. Bigger paycheck.',
		textTwo:
			'Get better-paying job offers without the hassle. Use them to negotiate a raise where you are—or take the new job. Either way, you win.',
		image: heroRight,
		imageMobile: heroRight,
	};

	const pageContent = content || defaultContent;

	return (
		<div className="max-w-[1300px] mx-auto sm:px-6 lg:px-8 pt-4 md:pt-16 pb-16">
			<div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
				{/* Right Column - Image with infinite scroll */}
				<div className="mb-[24px] md:w-[49%] md:mb-0 lg:mb-0 lg:order-2 lg:ml-12">
					{media ? (
						media
					) : (
						<>
							{/* Desktop version with infinite vertical scroll */}
							<div
								className="hero-scroll-anim hidden md:block w-full h-[600px] rounded-3xl"
								style={{
									backgroundImage: `url(${pageContent.image})`,
									backgroundSize: 'cover',
									backgroundPosition: 'center',
									animation: 'scrollVerticalSeamless 150s linear infinite',
								}}
							/>

							{/* Mobile version - horizontal scroll */}
							<div
								className="hero-scroll-anim block md:hidden w-full h-[300px] rounded-1xl overflow-hidden"
								style={{
									backgroundImage: `url(${pageContent.imageMobile})`,
									backgroundSize: 'cover',
									backgroundPosition: 'center',
									animation: 'scrollHorizontalSeamless 250s linear infinite',
								}}
							/>
						</>
					)}
				</div>

				{/* Left Column */}
				<div className="md:max-w-[49%] lg:order-1 px-4 md:px-0">
					<div className="mb-[12px] md:mb-8">
						<AvatarGroup avatarText={content?.avatarText} />
					</div>

					<h1 className={headingStyles}>{pageContent.textOne}</h1>

					{displayChecklist && !isMobile && (
						<div className="mb-[12px] md:mb-8">
							<Checklist content={checklistContent} />
						</div>
					)}

					{pageContent.textTwo && <p className="text-[18px] text-gray-600 mb-6 md:mb-8">{pageContent.textTwo}</p>}
					{pageContent.textThree && (
						<p className={`${textThreeStyles} text-[18px] mb-6 md:mb-8`}>{pageContent.textThree}</p>
					)}

					<div className="flex flex-col sm:flex-row gap-4">
						{showFirstButton && (
							<button
								onClick={() => navigateToQuiz(1)}
								className="bg-black text-white flex justify-center items-center gap-[10px] px-6 py-4 rounded-[100px] font-semibold transition-colors w-full sm:w-auto hover:bg-opacity-90"
							>
								{firstButtonText ? firstButtonText : 'Find out what you should earn'}
							</button>
						)}

						{showSecondButton && (
							<button
								onClick={() => navigateToCheckout()}
								className="bg-[#EBEBEB] text-black flex justify-center items-center gap-[10px] px-6 py-4 rounded-[100px] font-semibold transition-colors w-full sm:w-auto hover:bg-gray-200"
							>
								{secondButtonText ? secondButtonText : `Get started with ${brandName}`}
							</button>
						)}
						{page === 'homepage' && (
							<button
								onClick={handleScrollToHowItWorks}
								className="bg-[#EBEBEB] text-black flex justify-center items-center gap-[10px] px-6 py-4 rounded-[100px] font-semibold transition-colors w-full sm:w-auto hover:bg-gray-200"
							>
								See how it works
							</button>
						)}
						{displayChecklist && isMobile && (
							<section className="mt-2">
								<Checklist content={checklistContent} />
							</section>
						)}
					</div>
				</div>
			</div>

			{/* CSS Animations */}
			<style>{`
				@media (prefers-reduced-motion: reduce) {
					.hero-scroll-anim {
						animation: none !important;
					}
				}

				@keyframes scrollVerticalSeamless {
					0% {
						background-position: center 0%;
					}
					100% {
						background-position: center -1000%;
					}
				}
				
				@keyframes scrollHorizontalSeamless {
					0% {
						background-position: -1000% center;
					}
					100% {
						background-position: 0% center;
					}
				}
			`}</style>
		</div>
	);
};

export default HeroSectionSlider;